import { useState, useEffect } from 'react';

import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { IS_INDIAN_EXCHANGE, NUMBER_TYPES } from 'constants/constants';
import { CURRENCY_STYLE, CURRENCY_SYMBOL } from 'constants/currency';
import { convertFromINRtoUSD, noop } from 'helpers/utils';
import { inrConversionRateSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import Box from 'UIKit/Box';
import NumberInput from 'UIKit/NumberInput';

import styles from './amountInput.module.scss';

interface IAmountInputProps {
  amount?: null | string;
  updateState: ({ amount }: { amount: string }) => void;
  selectedCoin: string;
  handleMaxAmountClick: () => void;
  inputMode?: string;
  negativeValueAllowed?: boolean;
  errorMessage?: string;
}

const AmountInput = ({
  amount = null,
  updateState,
  selectedCoin,
  handleMaxAmountClick,
  inputMode = 'decimal',
  negativeValueAllowed = false,
  errorMessage = null,
}: IAmountInputProps) => {
  const { t } = useTranslation(['account']);
  const inrConversionRate = useAppSelector(inrConversionRateSelector);
  const [usdEquivalent, setUsdEquivalent] = useState<string | null>(null);

  useEffect(() => {
    if (IS_INDIAN_EXCHANGE && amount && inrConversionRate) {
      const usdValue = convertFromINRtoUSD(parseFloat(amount), inrConversionRate);
      setUsdEquivalent(usdValue);
    } else {
      setUsdEquivalent(null);
    }
  }, [amount, inrConversionRate]);

  return (
    <Box className={styles.limitPriceArea}>
      <Box
        className={classnames(styles.container, {
          [`${styles['error-border']}`]: errorMessage != null,
        })}
      >
        {selectedCoin && !IS_INDIAN_EXCHANGE && (
          <Box
            data-testid="maxAmountBtn"
            onClick={handleMaxAmountClick}
            className={styles.bestOfferBtn}
            onKeyDown={noop}
            role="button"
            tabIndex={0}
          >
            {t('account:subAccount.transferFundModal.max')}
          </Box>
        )}

        {IS_INDIAN_EXCHANGE && usdEquivalent && (
          <Box
            className={styles.usdEquivalent}
          >{`${CURRENCY_SYMBOL.INR} ${amount} = ${CURRENCY_SYMBOL.USD} ${usdEquivalent}`}</Box>
        )}

        <NumberInput
          data-testid="amountInput"
          numberType={NUMBER_TYPES.decimal}
          value={amount}
          onChange={e => {
            updateState({
              amount: e.target.value,
            });
          }}
          autoComplete="off"
          className={`${styles[`limit-price`]}`}
          name="orderPrice"
          inputMode={inputMode}
          negativeValueAllowed={negativeValueAllowed}
          placeholder={undefined}
          onClick={undefined}
          inputRef={undefined}
        />

        <Box
          data-testid="symbolLabel"
          onClick={handleMaxAmountClick}
          className={styles.symbolLabel}
          onKeyDown={noop}
          role="button"
          tabIndex={0}
        >
          {IS_INDIAN_EXCHANGE ? CURRENCY_STYLE.INDIAN : selectedCoin}
        </Box>
      </Box>
    </Box>
  );
};

export default AmountInput;
